import { Localized, useLocalization } from "@fluent/react";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    Button,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import { AppTextFieldHandler } from "../../../../app/appTypes";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { CancelButton } from "../../../../app/components/CancelButton";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { performProcessAction, ProcessActionType, ProjectProcess } from "../../projectProcess/ProjectProcessApiClient";


export interface PerformActionOnProcessDialogProps {
    process: ProjectProcess;
    actionType: ProcessActionType;
    onSuccess: (process: ProjectProcess) => void;
    onCancel: () => void;
}

type ActionConfig = {
    titleId: string;
    titleFallback: string;
    warningId: string;
    warningFallback: string;
    confirmId: string;
    confirmFallback: string;
    requirePassword: boolean;
    requiredComment: boolean;
    commentLabelId: string;
    commentLabelFallback: string;
    commentHelperId?: string;
    commentHelperFallback?: string;
};
const ACTION_CONFIG: Record<ProcessActionType, ActionConfig> = {
    startProcess: {
        titleId: "process-start-confirmation",
        titleFallback: "Rozpoczęcie procesu",
        warningId: "process-start-warning",
        warningFallback: "Czy na pewno chcesz rozpocząć / wznowić proces?",
        confirmId: "process-start-confirm",
        confirmFallback: "Rozpocznij",
        requirePassword: true,
        requiredComment: true,
        commentLabelId: "process-start-comment-label",
        commentLabelFallback: "Komentarz do rozpoczęcia / wznowienia",
        commentHelperId: "process-start-comment-helper",
        commentHelperFallback: "Opisz powód rozpoczęcia lub wznowienia procesu."
    },
    pauseProcess: {
        titleId: "process-pause-confirmation",
        titleFallback: "Wstrzymanie procesu",
        warningId: "process-pause-warning",
        warningFallback: "Czy na pewno chcesz wstrzymać ten proces?",
        confirmId: "process-pause-confirm",
        confirmFallback: "Wstrzymaj",
        requirePassword: true,
        requiredComment: true,
        commentLabelId: "process-pause-comment-label",
        commentLabelFallback: "Powód wstrzymania procesu",
        commentHelperId: "process-pause-comment-helper",
        commentHelperFallback: "Opisz dlaczego proces jest wstrzymywany."
    },
    cancelProcess: {
        titleId: "process-cancel-confirmation",
        titleFallback: "Anulowanie procesu",
        warningId: "process-cancel-warning",
        warningFallback: "Czy na pewno chcesz anulować ten proces?",
        confirmId: "process-cancel-confirm",
        confirmFallback: "Anuluj proces",
        requirePassword: true,
        requiredComment: true,
        commentLabelId: "process-cancel-comment-label",
        commentLabelFallback: "Powód anulowania procesu",
        commentHelperId: "process-cancel-comment-helper",
        commentHelperFallback: "Opisz dlaczego proces jest anulowany."
    },
    finishProcess: {
        titleId: "process-finish-confirmation",
        titleFallback: "Zakończenie procesu",
        warningId: "process-finish-warning",
        warningFallback: "Czy na pewno chcesz zakończyć proces?",
        confirmId: "process-finish-confirm",
        confirmFallback: "Zakończ",
        requirePassword: true,
        requiredComment: false,
        commentLabelId: "process-finish-comment-label",
        commentLabelFallback: "Komentarz (opcjonalnie)",
        commentHelperId: "process-finish-comment-helper",
        commentHelperFallback: "Możesz dodać komentarz do zakończenia procesu."
    },
};

export const PerformActionOnProcessDialog = ({
    process,
    actionType,
    onSuccess,
    onCancel,
}: PerformActionOnProcessDialogProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const [password, setPassword] = useState("");
    const [actionComment, setActionComment] = useState("");
    const [commentError, setCommentError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<boolean>(false);

    const config = useMemo(() => ACTION_CONFIG[actionType], [actionType]);
    const { l10n } = useLocalization()

    const handlePasswordFieldChange: AppTextFieldHandler = useCallback(e => {
        setPassword(e.target.value);
        if (passwordError) {
            setPasswordError(false);
        }
    }, [passwordError]);

    const handleActionCommentChange: AppTextFieldHandler = useCallback(e => {
        setActionComment(e.target.value);
        if (commentError) {
            setCommentError(null);
        }
    }, [commentError]);

    const handleConfirmActionClick = async () => {
        if (config.requiredComment && !actionComment.trim()) {
            setCommentError("comment-required");
            return;
        }

        if (config.requirePassword && !password) {
            setPasswordError(true);
            return;
        }

        setSaving(true);
        setServerError("");
        try {
            if (process.id && actionType) {
                const result = await dispatch(performProcessAction({
                    processId: process.id,
                    action: actionType,
                    actionComment: actionComment,
                    password: password
                }));

                if (performProcessAction.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"));
                    const payload = result.payload as ProjectProcess;
                    onSuccess(payload);
                } else {
                    let errorMsg = "error";
                    if (result.payload && result.payload.kind === "http") {
                        const problem = result.payload.problem;
                        if (problem) {
                            errorMsg = problem.title;
                        }
                    }
                    setServerError(errorMsg);
                }
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: {
                    minWidth: { xs: "auto", sm: 300 },
                },
            }}
        >
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}
            >
                <Localized id={config.titleId}>
                    <span>{config.titleFallback}</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
                    <Stack spacing={1.5} sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
                            <WarningAmberIcon color="error" />
                            <Typography
                                variant="body2"
                                color="error"
                            >
                                <Localized id={config.warningId}>
                                    <span>{config.warningFallback}</span>
                                </Localized>
                            </Typography>
                        </Stack>

                        {/* Komentarz do akcji */}
                        <TextField
                            sx={{ marginTop: 1 }}
                            fullWidth
                            autoFocus
                            required={config.requiredComment}
                            multiline
                            minRows={2}
                            value={actionComment}
                            onChange={handleActionCommentChange}
                            label={l10n.getString(config.commentLabelId, null, config.commentLabelFallback)}
                            error={!!commentError}
                            helperText={
                                commentError ? (
                                    <Localized id="comment-required">
                                        <span>Komentarz jest wymagany dla tej akcji.</span>
                                    </Localized>
                                ) : config.commentHelperId ? (
                                    <Localized id={config.commentHelperId}>
                                        <span>{config.commentHelperFallback}</span>
                                    </Localized>
                                ) : undefined
                            }
                        />

                        {config.requirePassword && (
                            <TextField
                                sx={{ marginTop: 1 }}
                                fullWidth
                                type="password"
                                value={password}
                                onChange={handlePasswordFieldChange}
                                label={
                                    <Localized id="password">
                                        <span>Hasło</span>
                                    </Localized>
                                }
                                error={passwordError}
                                helperText={
                                    <Localized id="autorization-password-required">
                                        <span>Wymagana autoryzacja hasłem</span>
                                    </Localized>
                                }
                                autoComplete="off"
                            />
                        )}

                    </Stack>
                </Box>

                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    disabled={saving}
                    onClick={handleConfirmActionClick}
                >
                    <Localized id={config.confirmId}>
                        <span>{config.confirmFallback}</span>
                    </Localized>
                </Button>
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};

